// api.js - Base API utilities for making authenticated requests
import { useAuthStore } from '../state';
import { refreshAccessToken } from './tokenService';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

/**
 * Makes a fetch request with the current access token
 * Handles token refresh if needed
 * 
 * @param {string} endpoint - API endpoint to call
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
const fetchWithToken = async (endpoint, options = {}) => {
  // Get values directly from the store
  let accessToken = useAuthStore.getState().accessToken;
  const tokenExpiry = useAuthStore.getState().tokenExpiry;

  // Construct the full URL
  const url = endpoint.startsWith('http') ? endpoint : `${apiBaseUrl}${endpoint}`;

  // Check if token is expired or will expire soon (within 30 seconds)
  const isTokenExpired = tokenExpiry && tokenExpiry - Date.now() < 30000;

  // If token is expired, get a new access token using the refresh token in HTTP-only cookie
  if (!accessToken || isTokenExpired) {
    try {
      console.log('making refreshAccessToken call from fetchWithToken');
      accessToken = await refreshAccessToken();

      if (!accessToken) {
        // If refresh fails, redirect to login
        // window.location.href = '/login';
        throw new Error('Unable to refresh access token');
      }
    } catch (error) {
      console.error('Error refreshing access token:', error);
      throw error;
    }
  }

  const isFormData = options && options.body instanceof FormData;

  const headers = { ...(options.headers || {}) };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (process.env.NODE_ENV === 'development') {
    headers['ngrok-skip-browser-warning'] = 'true';
  }

  if (!isFormData && !('Content-Type' in headers)) {
    headers['Content-Type'] = 'application/json';
  }

  return fetch(url, {
    ...options,
    credentials: options.credentials || 'omit', // Explicitly prevent cookies from being sent
    headers
  });
};

export default fetchWithToken;
